import { useMemo } from 'react'

import { useIsSubscribed } from 'src/shared/hooks/use-is-subscribed'

import {
  BASE_BLOCKS,
  type Block,
  BusinessCreateConfig,
  BusinessCreateConfigWithWorkflows,
  type BCStepName,
  type StepItem,
  SUBSCRIBED_BLOCKS,
} from '../constants/bc-config'
import { useBusinessCreateStore } from '../store/business-create-store'

type StepMap = Partial<Record<BCStepName, StepItem>>

const CLOSED_DATE_PROGRESS_INDEX = 2
const PERSONAL_INFO_BLOCK_STEPS_WITH_CLOSED_DATE = 6
const PERSONAL_INFO_BLOCK_STEPS_WITHOUT_CLOSED_DATE = 5
const CLOSED_DATE_STEP_KEY: BCStepName = 'closed-date'
const PERSONAL_INFO_BLOCK_KEY = 'personal-info'

const shouldShiftPersonalInfoProgress = (step: StepItem) => {
  return step.progress.block === 0 && step.progress.step > CLOSED_DATE_PROGRESS_INDEX
}

const buildStepsForFlow = (sourceSteps: StepMap, includeClosedDateStep: boolean): StepMap => {
  if (includeClosedDateStep) {
    return sourceSteps
  }

  const flowSteps: StepMap = {}
  const orderedStepKeys = Object.keys(sourceSteps) as BCStepName[]

  for (const stepKey of orderedStepKeys) {
    if (stepKey === CLOSED_DATE_STEP_KEY) continue

    const step = sourceSteps[stepKey]
    if (!step) continue

    if (!shouldShiftPersonalInfoProgress(step)) {
      flowSteps[stepKey] = step
      continue
    }

    flowSteps[stepKey] = {
      ...step,
      progress: {
        ...step.progress,
        step: step.progress.step - 1,
      },
    }
  }

  return flowSteps
}

const getPersonalInfoStepCount = (isClosedBusiness: boolean) => {
  return isClosedBusiness ? PERSONAL_INFO_BLOCK_STEPS_WITH_CLOSED_DATE : PERSONAL_INFO_BLOCK_STEPS_WITHOUT_CLOSED_DATE
}

const buildBlocksForFlow = (sourceBlocks: Block[], personalInfoStepCount: number): Block[] => {
  const flowBlocks: Block[] = []

  for (const block of sourceBlocks) {
    if (block.key !== PERSONAL_INFO_BLOCK_KEY) {
      flowBlocks.push(block)
      continue
    }

    flowBlocks.push({
      ...block,
      blockSteps: personalInfoStepCount,
    })
  }

  return flowBlocks
}

const useCreateBusinessConfig = () => {
  const isSubscribed = useIsSubscribed()
  const { formData } = useBusinessCreateStore()

  const isClosedBusiness = formData.isClosed === true
  const shouldIncludeClosedDateStep = formData.isClosed !== false

  const config = useMemo(() => {
    const sourceConfig = isSubscribed ? BusinessCreateConfigWithWorkflows : BusinessCreateConfig
    const flowSteps = buildStepsForFlow(sourceConfig.steps, shouldIncludeClosedDateStep)

    return {
      ...sourceConfig,
      steps: flowSteps,
      totalSteps: Object.keys(flowSteps).length,
    }
  }, [isSubscribed, shouldIncludeClosedDateStep])

  const totalSteps = config.totalSteps

  const blocks = useMemo(() => {
    const sourceBlocks = isSubscribed ? SUBSCRIBED_BLOCKS : BASE_BLOCKS
    const personalInfoStepCount = getPersonalInfoStepCount(isClosedBusiness)
    return buildBlocksForFlow(sourceBlocks, personalInfoStepCount)
  }, [isClosedBusiness, isSubscribed])

  return { config, blocks, totalSteps }
}

export default useCreateBusinessConfig
